import InternalChatBadge from '@/components/InternalChatBadge'
import MyAlertsDropdown from '@/components/my-alerts/my-alerts-dropdown'
import PresenceBadge from '@/components/presence/presence-badge'
import SalesPipelineDropdown from '@/components/sales/SalesPipelineDropdown'
import ShortcutsDropdown from '@/components/ShortcutsDropdown'
import { CompanySelector } from '@/components/company/CompanySelector'
import { GlobalSearchInput } from '@/components/global-search/GlobalSearchInput'
import NotificationDropdown from '@/components/notifications/NotificationDropdown'
import { CompanySegment } from '@/lib/api/common/interfaces/company.interface'
import { getFileUrl } from '@/lib/utils/file-url'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { logout } from '@/store/slices/authSlice'
import {
    removeWaitingForHuman,
    WaitingForHumanNotification,
} from '@/store/slices/notificationsSlice'
import { BsPersonBadge } from 'react-icons/bs'
import {
    MdLogout,
    MdMenu,
    MdShoppingCart,
    MdStorefront,
    MdSupportAgent,
} from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'
import { endServerSession } from '@/lib/api/session-refresh'

interface AdminNavbarProps {
    onMenuClick: () => void
}

const breadcrumbMap: Record<string, { label: string; path: string }[]> = {
    '/admin': [{ label: 'Dashboard', path: '/admin' }],
    '/atendimento/chats': [
        { label: 'Dashboard', path: '/' },
        { label: 'Atendimentos', path: '/atendimento/chats' },
    ],
    '/atendimento/sectors': [
        { label: 'Dashboard', path: '/' },
        { label: 'Setores', path: '/atendimento/sectors' },
    ],
    '/atendimento/sectors/new': [
        { label: 'Dashboard', path: '/' },
        { label: 'Setores', path: '/atendimento/sectors' },
        { label: 'Adicionar', path: '/atendimento/sectors/new' },
    ],
    '/atendimento/sectors/edit': [
        { label: 'Dashboard', path: '/' },
        { label: 'Setores', path: '/atendimento/sectors' },
        { label: 'Editar', path: '' },
    ],
    '/atendimento/agents': [
        { label: 'Dashboard', path: '/' },
        { label: 'Atendentes', path: '/atendimento/agents' },
    ],
    '/atendimento/agents/new': [
        { label: 'Dashboard', path: '/' },
        { label: 'Atendentes', path: '/atendimento/agents' },
        { label: 'Adicionar', path: '/atendimento/agents/new' },
    ],
    '/atendimento/agents/edit': [
        { label: 'Dashboard', path: '/' },
        { label: 'Atendentes', path: '/atendimento/agents' },
        { label: 'Editar', path: '' },
    ],
    '/hr/employees': [
        { label: 'Dashboard', path: '/' },
        { label: 'Funcionários', path: '/hr/employees' },
    ],
    '/hr/employees/new': [
        { label: 'Dashboard', path: '/' },
        { label: 'Funcionários', path: '/hr/employees' },
        { label: 'Adicionar', path: '/hr/employees/new' },
    ],
    '/hr/employees/edit': [
        { label: 'Dashboard', path: '/' },
        { label: 'Funcionários', path: '/hr/employees' },
        { label: 'Editar', path: '' },
    ],
    '/account': [
        { label: 'Dashboard', path: '/' },
        { label: 'Minha Conta', path: '/account' },
    ],
    '/system-store': [
        { label: 'Dashboard', path: '/' },
        { label: 'Loja do Sistema', path: '/system-store' },
    ],
}

const getBreadcrumbs = (pathname: string) => {
    if (/^\/admin\/atendimentos\/\d+$/gi.test(pathname)) {
        return []
    }

    // Check for edit routes with IDs
    if (
        pathname.match(
            /\/admin\/(produtos|setores|atendentes|funcionarios)\/edit\/\d+$/
        )
    ) {
        const basePath = pathname.replace(/\/edit\/\d+$/, '')
        const key = basePath + '/edit'
        return breadcrumbMap[key] || breadcrumbMap[basePath] || []
    }

    // Check exact matches first
    if (breadcrumbMap[pathname]) {
        return breadcrumbMap[pathname]
    }

    // Fallback to base path
    const basePath = pathname.split('/').slice(0, 3).join('/')
    return breadcrumbMap[basePath] || breadcrumbMap['/admin'] || []
}

export const AdminNavbar = ({ onMenuClick }: AdminNavbarProps) => {
    const { user, isCompany } = useAuth()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const waitingForHuman = useAppSelector(
        (state: any) => state.notifications?.waitingForHuman || []
    )
    const currentCompanyId = user?.companyId

    const handleLogout = async () => {
        const { logoutOneSignal } = await import('@/lib/utils/onesignal-logout')
        await endServerSession()
        await logoutOneSignal()
        dispatch(logout())
        navigate('/login')
    }

    const handleNotificationClick = (chatId: number) => {
        dispatch(removeWaitingForHuman(chatId))
        navigate(`/atendimento/chats/${chatId}`)

        const dropdown = document.activeElement as HTMLElement
        if (dropdown) dropdown.blur()
    }

    const planName =
        (user?.company as any)?.subscription?.plan?.name || 'Administração'

    return (
        <header className="bg-base-100 border-b border-base-300 relative">
            {/*
                `relative` no header é a âncora dos menus em telas pequenas: lá os
                wrappers `.dropdown` viram `static` e o conteúdo se posiciona contra
                o header (largura da tela, logo abaixo dele) em vez de sair pela
                lateral — um menu de 24rem alinhado a um botão que não encosta na
                borda direita transbordava para fora, pela esquerda.


                Celular (< 640px): o header quebra em duas linhas (`flex-wrap`). A
                primeira mantém o essencial sempre alcançável com o polegar — menu,
                empresa, notificações e a conta do usuário (por onde se sai do
                sistema) — e a faixa de ações desce para a segunda via `order-last
                w-full`. Antes tudo disputava uma linha só: o bloco da conta era
                empurrado para fora da viewport e só aparecia deitando o aparelho.
                De `sm` para cima (tablet inclusive) volta a ser uma linha só de
                64px — os rótulos dos botões é que esperam `lg` para aparecer.
            */}
            <div className="flex flex-wrap items-center gap-y-1 px-2 py-2 sm:h-16 sm:flex-nowrap sm:justify-between sm:gap-y-0 sm:px-3 sm:py-0">
                <div className="flex min-w-0 items-center gap-2 sm:gap-4">
                    <div
                        className="lg:hidden btn btn-ghost btn-sm px-2"
                        onClick={onMenuClick}
                    >
                        <MdMenu className="text-xl" />
                    </div>
                    <div className="flex min-w-0 items-center gap-3">
                        <CompanySelector />
                        {/* No começo da barra, junto do seletor de empresa: é o primeiro
                            lugar onde o olho cai, e alerta que ninguém vê não serve para
                            nada — foi essa a reclamação que criou o recurso. */}
                        <MyAlertsDropdown />
                        {/* <h2 className="text-lg font-semibold text-base-content hidden sm:block">
                            {planName}
                        </h2>
                        {(user?.company as any)?.subscription && (
                            <button
                                className="btn btn-sm btn-outline gap-2"
                                onClick={() => navigate('/conta#assinatura')}
                                title="Gerenciar assinatura"
                            >
                                <MdManageAccounts className="text-base" />
                                <span className="hidden md:inline">
                                    Gerenciar
                                </span>
                            </button>
                        )}*/}
                    </div>
                </div>

                {/* Busca Global
                <div className="hidden md:block flex-1 max-w-md mx-4">
                    <GlobalSearchInput />
                </div>
                */}
                <div className="order-last flex w-full flex-wrap items-center justify-start gap-1 sm:order-none sm:w-auto sm:flex-1 sm:flex-nowrap sm:justify-center sm:gap-2">
                    <div className="dropdown dropdown-bottom max-sm:!static">
                        <div
                            tabIndex={0}
                            role="button"
                            className={`btn gap-2 indicator ${
                                waitingForHuman.length > 0
                                    ? 'btn-info !text-white'
                                    : 'btn-ghost'
                            }`}
                            title="Aguardando atendimento"
                        >
                            {/* Só o ícone: o texto disputava espaço com o que a
                                operação olha o dia inteiro, e o `title` já diz o que é
                                ao passar o mouse. O contador continua visível — é ele
                                que importa aqui. */}
                            <BsPersonBadge className="text-xl" />
                            {waitingForHuman.length > 0 && (
                                <span className="badge badge-warning badge-sm indicator-item">
                                    {waitingForHuman.length}
                                </span>
                            )}
                        </div>
                        <ul
                            tabIndex={0}
                            className="dropdown-content menu bg-base-100 rounded-box z-[1] w-96 max-sm:!inset-x-2 max-sm:!w-auto max-h-[70vh] overflow-y-auto p-2 shadow-lg border border-base-200 mt-2"
                        >
                            <li className="menu-title px-4 py-2">
                                <span className="text-sm font-semibold">
                                    Aguardando Atendimento
                                </span>
                            </li>
                            {waitingForHuman.length === 0 ? (
                                <li className="px-4 py-3">
                                    <span className="text-sm text-base-content/60">
                                        Nenhuma solicitação
                                    </span>
                                </li>
                            ) : (
                                waitingForHuman.map(
                                    (
                                        notification: WaitingForHumanNotification
                                    ) => (
                                        <li key={notification.chatId}>
                                            <a
                                                onClick={() =>
                                                    handleNotificationClick(
                                                        notification.chatId
                                                    )
                                                }
                                                className="flex flex-col items-start gap-1 py-3 px-4 hover:bg-base-200 rounded-lg cursor-pointer"
                                            >
                                                <div className="flex justify-between items-start w-full">
                                                    <span className="font-medium text-sm">
                                                        {notification.name}
                                                    </span>
                                                    <span className="text-xs text-base-content/60">
                                                        {new Date(
                                                            notification.timestamp
                                                        ).toLocaleTimeString(
                                                            'pt-BR',
                                                            {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            }
                                                        )}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-base-content/70">
                                                    {notification.phoneNumber}
                                                </span>
                                                <span className="text-xs text-base-content/60">
                                                    {notification.reason}
                                                </span>
                                            </a>
                                        </li>
                                    )
                                )
                            )}
                        </ul>
                    </div>

                    <PresenceBadge />
                </div>

                {/* `ml-auto` + `shrink-0`: a conta do usuário fica colada à direita e
                    nunca é comprimida/empurrada para fora da tela pelos vizinhos. */}
                <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2 lg:gap-4">
                    <SalesPipelineDropdown />
                    <NotificationDropdown />

                    <div className="dropdown dropdown-bottom dropdown-end max-sm:!static">
                        <div
                            tabIndex={0}
                            role="button"
                            className="flex items-center gap-2 lg:gap-3 cursor-pointer hover:bg-base-200 rounded-lg p-1"
                        >
                            <div className="text-right hidden md:block max-w-[160px]">
                                <p className="text-sm font-medium text-base-content truncate">
                                    {(user?.company as any)?.tradingName ||
                                        user?.company?.name ||
                                        user?.name ||
                                        'Usuário'}
                                </p>
                                <p className="text-xs text-base-content/70 truncate">
                                    {user?.name || user?.email || ''}
                                </p>
                            </div>
                            {/*
                                A foto do usuário tem precedência sobre a inicial —
                                a inicial é o fallback de quem ainda não enviou uma.
                                A troca na aba "Conta" atualiza `user.picture` no
                                Redux, então isto reflete na hora, sem recarregar.
                            */}
                            <div className="avatar placeholder">
                                {user?.picture ? (
                                    <div className="rounded-full w-10 h-10 overflow-hidden">
                                        <img
                                            src={getFileUrl(user.picture)}
                                            alt={user?.name || 'Usuário'}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="bg-secondary text-secondary-content rounded-full w-10 h-10 flex items-center justify-center">
                                        <span className="text-sm font-bold">
                                            {(
                                                (user?.company as any)
                                                    ?.tradingName ||
                                                user?.company?.name ||
                                                user?.name ||
                                                'U'
                                            )
                                                .charAt(0)
                                                .toUpperCase()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <ul
                            tabIndex={0}
                            className="dropdown-content menu bg-base-100 rounded-box z-[60] mt-2 w-56 max-sm:!right-2 max-sm:!left-auto p-2 shadow-lg border border-base-300/50"
                        >
                            <li>
                                <button
                                    onClick={() => {
                                        ;(
                                            document.activeElement as HTMLElement
                                        )?.blur()
                                        navigate('/account')
                                    }}
                                >
                                    <MdMenu className="text-lg" /> Minha Conta
                                </button>
                            </li>
                            {/* Compra de créditos de IA e recursos extras: só o
                                titular da conta compra. */}
                            {isCompany && (
                                <li>
                                    <button
                                        onClick={() => {
                                            ;(
                                                document.activeElement as HTMLElement
                                            )?.blur()
                                            navigate('/system-store')
                                        }}
                                    >
                                        <MdStorefront className="text-lg" /> Loja
                                        do Sistema
                                    </button>
                                </li>
                            )}
                            {/* Movido da barra para cá em 30/09/2026: quase ninguém usa
                                o chat interno, e ele ocupava lugar nobre. O contador veio
                                junto — sem ele a mensagem vira invisível. */}
                            <li>
                                <ShortcutsDropdown variant="menu-item" />
                            </li>
                            <li>
                                <InternalChatBadge variant="menu-item" />
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        ;(
                                            document.activeElement as HTMLElement
                                        )?.blur()
                                        navigate('/support')
                                    }}
                                >
                                    <MdSupportAgent className="text-lg" /> Ajuda
                                    e Suporte
                                </button>
                            </li>
                            <div className="divider my-1" />
                            <li>
                                <button
                                    onClick={() => {
                                        ;(
                                            document.activeElement as HTMLElement
                                        )?.blur()
                                        handleLogout()
                                    }}
                                    className="!text-error"
                                >
                                    <MdLogout className="text-lg" /> Sair
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/*breadcrumbs.length > 1 && (
                <div className="px-6 py-2 border-t border-base-300/50 bg-base-200/30">
                    <nav className="flex items-center gap-2 text-sm">
                        <Link
                            to="/admin"
                            className="flex items-center gap-1 text-base-content/60 hover:text-secondary transition-colors"
                        >
                            <MdHome className="text-base" />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Link>
                        {breadcrumbs.slice(1).map((crumb, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-2"
                            >
                                <MdChevronRight className="text-base-content/40 text-base" />
                                {crumb.path ? (
                                    <Link
                                        to={crumb.path}
                                        className="text-base-content/60 hover:text-secondary transition-colors"
                                    >
                                        {crumb.label}
                                    </Link>
                                ) : (
                                    <span className="text-base-content font-medium">
                                        {crumb.label}
                                    </span>
                                )}
                            </div>
                        ))}
                    </nav>
                </div>
            )*/}
        </header>
    )
}
